import { useState } from "react";
import { CheckCircle, Clock, Calendar, Mail, User, MoreVertical, Edit3, Trash2, PlayCircle, PauseCircle, StickyNote } from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Textarea } from "../ui/textarea";
import { Progress } from "../ui/progress";
import { Checkbox } from "../ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "../ui/dropdown-menu";

export const progressTextClass = (pct) => {
  if (pct === 100) return "text-emerald-600";
  if (pct >= 50) return "text-blue-600";
  return "text-slate-600";
};

export const progressBarClass = (pct) => {
  if (pct === 100) return "[&>div]:bg-emerald-500";
  if (pct >= 50) return "[&>div]:bg-blue-500";
  return "[&>div]:bg-slate-400";
};

const customerCardClass = (customer, isOverdue) => {
  if (customer.status === "completed") return "border-emerald-200 bg-emerald-50/30";
  if (customer.status === "paused") return "border-amber-200 bg-amber-50/30";
  return isOverdue ? "border-red-200" : "border-slate-200";
};

const stepBoxClass = (step, isOverdue) => {
  if (step.is_completed) return "bg-emerald-50 border-emerald-200";
  return isOverdue ? "bg-red-50 border-red-200" : "bg-white border-slate-200";
};

const stepBadgeClass = (step, isOverdue) => {
  if (step.is_completed) return "border-emerald-200 text-emerald-600";
  return isOverdue ? "border-red-200 text-red-600 bg-red-50" : "border-slate-200";
};

export const StatCard = ({ icon: Icon, label, value, color, subtext }) => (
  <Card className="border-slate-200">
    <CardContent className="p-4">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg ${color} flex items-center justify-center`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div>
          <p className="text-2xl font-bold text-slate-900">{value}</p>
          <p className="text-sm text-slate-500">{label}</p>
          {subtext && <p className="text-xs text-slate-400">{subtext}</p>}
        </div>
      </div>
    </CardContent>
  </Card>
);

export const CustomerCard = ({ customer, onClick, onStatusChange, onDelete }) => {
  const progress = customer.progress || { total: 0, completed: 0, percentage: 0 };
  const isOverdue = customer.steps?.some(s => 
    s.due_date && !s.is_completed && new Date(s.due_date) < new Date()
  );
  
  return (
    <Card 
      className={`border cursor-pointer transition-all hover:shadow-md ${
        customerCardClass(customer, isOverdue)
      }`}
      onClick={onClick}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold text-slate-900 truncate">{customer.name}</h3>
              {customer.status === "completed" && (
                <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Completed
                </Badge>
              )}
              {customer.status === "paused" && (
                <Badge className="bg-amber-100 text-amber-700 border-amber-200">
                  <PauseCircle className="w-3 h-3 mr-1" />
                  Paused
                </Badge>
              )}
              {isOverdue && customer.status === "active" && (
                <Badge className="bg-red-100 text-red-700 border-red-200">
                  <Clock className="w-3 h-3 mr-1" />
                  Overdue
                </Badge>
              )}
            </div>
            <p className="text-sm text-slate-600 mb-2">{customer.company || "No company"}</p>
            
            <div className="flex items-center gap-4 text-xs text-slate-500 mb-3">
              <span className="flex items-center gap-1">
                <Mail className="w-3 h-3" />
                {customer.email}
              </span>
              {customer.contact_person && (
                <span className="flex items-center gap-1">
                  <User className="w-3 h-3" />
                  {customer.contact_person}
                </span>
              )}
            </div>
            
            {/* Progress Bar */}
            <div className="space-y-1">
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-500">
                  {progress.completed} of {progress.total} steps
                </span>
                <span className={`font-medium ${progressTextClass(progress.percentage)}`}>
                  {progress.percentage}%
                </span>
              </div>
              <Progress 
                value={progress.percentage} 
                className={`h-2 ${progressBarClass(progress.percentage)}`}
              />
            </div>
          </div>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {customer.status === "active" && (
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onStatusChange("paused"); }}>
                  <PauseCircle className="w-4 h-4 mr-2" />
                  Pause Onboarding
                </DropdownMenuItem>
              )}
              {customer.status === "paused" && (
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onStatusChange("active"); }}>
                  <PlayCircle className="w-4 h-4 mr-2" />
                  Resume Onboarding
                </DropdownMenuItem>
              )}
              {customer.status !== "completed" && (
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onStatusChange("completed"); }}>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Mark Complete
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                onClick={(e) => { e.stopPropagation(); onDelete(); }}
                className="text-red-600"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Customer
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardContent>
    </Card>
  );
};

export const StepItem = ({ step, onToggle, onEdit, onDelete, onNotesUpdate }) => {
  const [notesOpen, setNotesOpen] = useState(false);
  const [notes, setNotes] = useState(step.notes || "");
  const isOverdue = step.due_date && !step.is_completed && new Date(step.due_date) < new Date();
  
  const handleSaveNotes = () => {
    onNotesUpdate(notes);
    setNotesOpen(false);
  };
  
  return (
    <div className={`p-3 rounded-lg border ${stepBoxClass(step, isOverdue)}`}>
      <div className="flex items-start gap-3">
        <Checkbox
          checked={step.is_completed}
          onCheckedChange={onToggle}
          className="mt-0.5"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={`font-medium ${step.is_completed ? "text-slate-500 line-through" : "text-slate-900"}`}>
              {step.title}
            </span>
            {step.due_date && (
              <Badge 
                variant="outline" 
                className={`text-xs ${stepBadgeClass(step, isOverdue)}`}
              >
                <Calendar className="w-3 h-3 mr-1" />
                {new Date(step.due_date).toLocaleDateString()}
              </Badge>
            )}
          </div>
          {step.description && (
            <p className={`text-sm mt-1 ${step.is_completed ? "text-slate-400" : "text-slate-600"}`}>
              {step.description}
            </p>
          )}
          {step.notes && (
            <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
              <StickyNote className="w-3 h-3 inline mr-1" />
              {step.notes}
            </div>
          )}
          {step.is_completed && step.completed_at && (
            <p className="text-xs text-emerald-600 mt-1">
              Completed {new Date(step.completed_at).toLocaleDateString()}
              {step.completed_by && ` by ${step.completed_by}`}
            </p>
          )}
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={() => setNotesOpen(true)}
          >
            <StickyNote className="w-4 h-4 text-slate-400" />
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={onEdit}>
                <Edit3 className="w-4 h-4 mr-2" />
                Edit Step
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onDelete} className="text-red-600">
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Step
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      
      {/* Notes Dialog */}
      <Dialog open={notesOpen} onOpenChange={setNotesOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Step Notes</DialogTitle>
            <DialogDescription>Add notes for: {step.title}</DialogDescription>
          </DialogHeader>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add notes about this step..."
            rows={4}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setNotesOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveNotes}>Save Notes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
